import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchAgentByUuid, saveOrUpdateAgent, processFile, deleteAgent } from "../../api/AgentApi";
import './AgentPage.scss';
import {Agent, AgentStatus, Integration} from "../model";
import {PageProps} from "../main/App";

const emptyAgent = {
  behaviour: "",
  name: "",
  status: AgentStatus.AWAITING_FILE_UPLOAD
}

const AgentPage: React.FC<PageProps> = ({ showNotification }) => {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const [originalAgent, setOriginalAgent] = useState<Agent | null>(null);
  const [agent, setAgent] = useState<Agent | null>(null);
  const [ragDataFile, setRagDataFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const isNewAgent = !uuid;
  const isAgentChanged = JSON.stringify(agent) !== JSON.stringify(originalAgent);

  useEffect(() => {
    if (!isNewAgent) {
      const loadAgent = async () => {
        setLoading(true);
        try {
          const data = await fetchAgentByUuid(uuid);
          setAgent(data);
          setOriginalAgent(data);
        } catch (err) {
          setError('Failed to load agent');
        } finally {
          setLoading(false);
        }
      };
      loadAgent();
    } else {
      setAgent(emptyAgent);
      setOriginalAgent(emptyAgent);
    }
  }, [uuid, isNewAgent]);

  const handleSaveBasicAgent = async () => {
    setLoading(true);
    try {
      const savedAgent = await saveOrUpdateAgent(agent);
      setAgent(savedAgent);
      setOriginalAgent(savedAgent);
      navigate(`/agent/${savedAgent.uuid}`);
    } catch (err) {
      setError('Failed to save basic agent');
    } finally {
      setLoading(false);
    }
  };

  const handleProcessRagData = async () => {
    if (!ragDataFile) {
      setError("RAG data file is missing");
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', ragDataFile);

      await processFile(agent!.uuid!, formData, setUploadProgress);

      setAgent((prev: any) => ({ ...prev, status: AgentStatus.READY }));
      setOriginalAgent(agent);
    } catch (err) {
      setError('Failed to upload and process RAG data');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    try {
      if (agent) {
        await deleteAgent(agent.uuid!);
        navigate('/agents');  // Redirect to the agents list
      }
    } catch (err) {
      setError('Failed to delete agent');
    }
  };

  const isSaveDisabled = !isAgentChanged;
  const isRagDisabled = !agent || agent.status !== AgentStatus.AWAITING_FILE_UPLOAD;

  const getStatusBadge = (status?: AgentStatus) => {
    switch (status) {
      case AgentStatus.AWAITING_FILE_UPLOAD:
        return <span className="status-badge awaiting">Awaiting File Upload</span>;
      case AgentStatus.READY:
        return <span className="status-badge ready">Ready</span>;
      default:
        return <span className="status-badge default">Unknown</span>;
    }
  };

  if (agent === null) return <div>Loading...</div>;

  return (
    <div className="agent-page">
      <div className="header-container">
        <h1>{isNewAgent ? 'Create New Agent' : 'Agent Details'}</h1>
        {agent && getStatusBadge(agent.status)}
        <button className="save-button" onClick={handleSaveBasicAgent} disabled={isSaveDisabled || loading}>
          {loading ? 'Saving...' : 'Save Agent'}
        </button>
        {!isNewAgent && (
          <button className="delete-button" onClick={handleDelete} disabled={loading}>
            {loading ? 'Deleting...' : 'Delete Agent'}
          </button>
        )}
      </div>
      {error && <div className="error-message">{error}</div>}
      <div className="input-group">
        <label>
          Name:
          <input
            className="input-field"
            type="text"
            value={agent?.name || ""}
            onChange={(e) => setAgent({ ...agent, name: e.target.value  })}
          />
        </label>
      </div>
      <div className="input-group">
        <label>
          Behaviour:
          <textarea
            className="input-field"
            value={agent?.behaviour || ""}
            rows={5}
            onChange={(e) => setAgent({ ...agent, behaviour: e.target.value })}
          />
        </label>
      </div>

      {agent && agent.status === AgentStatus.AWAITING_FILE_UPLOAD && (
        <div className="input-group">
          <label>
            Upload RAG Data File:
            <input
              className="file-input"
              type="file"
              accept=".json"
              onChange={(e) => setRagDataFile(e.target.files ? e.target.files[0] : null)}
            />
          </label>
          {ragDataFile && (
            <button className="process-button" onClick={handleProcessRagData} disabled={isRagDisabled}>
              {uploading ? `Uploading ${uploadProgress}%` : 'Process RAG File'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default AgentPage;
